/**
 * Wayfinding breadcrumb trail for the app chrome.
 *
 * Renders the Customer › Site Group › Site › page hierarchy a user has
 * drilled into, as a row of linked context levels ending in the current
 * page. Shared by the desktop/tablet title bar and the phone top bar so
 * both read identically; pass `dense` for the cramped phone variant.
 *
 * Crumbs come from useBreadcrumbs (each carries a `type` that selects the
 * level icon, and a site-group crumb says whether the group is virtual, which
 * draws a cloud beside its name); the last crumb is treated as the current
 * page. A non-last
 * crumb without a `to` (the hook de-links crumbs that target the page being
 * viewed) renders as plain secondary text — context, not navigation.
 */
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloudIcon from '@mui/icons-material/Cloud';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DescriptionIcon from '@mui/icons-material/Description';
import HubIcon from '@mui/icons-material/Hub';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RoomIcon from '@mui/icons-material/Room';
import { Box, Breadcrumbs, Link as MuiLink } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';

// Context level → icon. Mirrors the LeftNav context icons so the same
// entity reads the same everywhere (customer / plant / structure).
const TYPE_ICON = {
  customer: AccountBalanceIcon,
  siteGroup: HubIcon,
  site: RoomIcon,
  reports: DescriptionIcon,
  services: MiscellaneousServicesIcon,
  billing: CurrencyExchangeIcon,
};

function CrumbInner({ type, label, showIcon, iconColor, isVirtual }) {
  const Icon = showIcon && type ? TYPE_ICON[type] : null;
  return (
    <>
      {Icon && <Icon sx={{ fontSize: 16, mr: 0.5, flexShrink: 0, color: iconColor, opacity: 0.85 }} />}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      {/* A virtual group holds sites another group owns — the same cloud the
          group grid marks it with. It rides through the dense variant, which
          drops the level icon: this one says what the label cannot. */}
      {isVirtual && <CloudIcon sx={{ fontSize: 16, ml: 0.5, flexShrink: 0, color: 'text.secondary' }} />}
    </>
  );
}

export default function ContextBreadcrumbs({ crumbs, dense = false }) {
  if (!crumbs || crumbs.length === 0) return null;

  const fontSize = dense ? '0.8rem' : '0.875rem';
  // Icons add scanning value with room to spare; on the cramped phone bar
  // they cost width we need for the labels, so the dense variant drops them.
  const showIcons = !dense;

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon sx={{ fontSize: dense ? 16 : 18, color: 'text.disabled' }} />}
      maxItems={dense ? 2 : 4}
      itemsBeforeCollapse={1}
      itemsAfterCollapse={1}
      sx={{
        '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
        '& .MuiBreadcrumbs-li': { minWidth: 0 },
        '& .MuiBreadcrumbs-separator': { mx: dense ? 0.25 : 0.5 },
      }}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;

        if (!isLast) {
          const nonLastCrumbSx = {
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            maxWidth: dense ? 150 : 220,
            color: 'text.secondary',
            fontSize,
            fontWeight: 500,
            py: 0.25,
          };

          if (crumb.to) {
            return (
              <MuiLink
                key={index}
                component={Link}
                to={crumb.to}
                underline="hover"
                sx={{
                  ...nonLastCrumbSx,
                  '&:visited': { color: 'text.secondary' },
                  '&:hover': { color: 'text.primary' },
                  '&:focus-visible': { color: 'text.primary', outline: 'none' },
                }}
              >
                <CrumbInner
                  type={crumb.type}
                  label={crumb.label}
                  showIcon={showIcons}
                  iconColor="inherit"
                  isVirtual={crumb.isVirtual}
                />
              </MuiLink>
            );
          }

          return (
            <Box key={index} sx={nonLastCrumbSx}>
              <CrumbInner
                type={crumb.type}
                label={crumb.label}
                showIcon={showIcons}
                iconColor="inherit"
                isVirtual={crumb.isVirtual}
              />
            </Box>
          );
        }

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              maxWidth: dense ? 200 : 280,
              color: 'text.primary',
              fontSize,
              fontWeight: 700,
            }}
          >
            <CrumbInner
              type={crumb.type}
              label={crumb.label}
              showIcon={showIcons}
              iconColor="primary.main"
              isVirtual={crumb.isVirtual}
            />
          </Box>
        );
      })}
    </Breadcrumbs>
  );
}

ContextBreadcrumbs.propTypes = {
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
      type: PropTypes.string,
      isVirtual: PropTypes.bool,
    }),
  ),
  dense: PropTypes.bool,
};
